import cn from 'classnames';
import React, {useEffect, useRef} from 'react';

import {
  CodonTranslationTable,
  DNA_COMPLEMENT_TABLE,
  NucleotideType,
  RNA_COMPLEMENT_TABLE,
} from 'biojs/BioConstants';
import {dnaToRNA, revcomp, rnaToDNA, translateOnStrand} from 'biojs/Util';
import {CopyPreferences, ModifiedNucleotideAttributes, SequenceType} from 'constants/SequenceConstants';
import BenchlingQuery, {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getUTFlexibleNaturalBasesIfEnabled} from 'modbio/util';
import {CopyNucleotideSequenceMode} from 'molbio/CopyUtil';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {MONOMER_QUERY} from 'molbio/monomers/MonomerConstants';
import {generateSimplePolymerHELM} from 'molbio/monomers/MonomersUtil';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Clipboard from 'ui-library/Clipboard';
import usePreference from 'ui-library/hooks/usePreference';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

interface CopyOption {
  value: string;
  copyType: CopyNucleotideSequenceMode;
  label: string;
  testKey: string;
  fullWidth?: boolean;
}
function CopyContainer({
  value,
  copyType,
  label,
  initialSelect,
  notifyCopied,
  testKey,
  fullWidth,
}: CopyOption & {
  initialSelect: CopyNucleotideSequenceMode;
  notifyCopied: (copyType: CopyNucleotideSequenceMode, copiedResidues: string) => void;
}): JSX.Element {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (initialSelect === copyType && ref.current) {
      ref.current.select();
    }
  });

  return (
    <div className={cn('col', fullWidth ? 'col-md-12' : 'col-md-6')}>
      <label>{label}</label>
      <Clipboard
        value={value}
        notifyCopied={() => {
          notifyCopied(copyType, value);
        }}
      >
        <textarea
          rows={5}
          readOnly={true}
          wrap="hard"
          className={`mono ${copyType}`}
          value={value}
          ref={ref}
          {...getTestProps({component: 'CopyContainer', element: 'textarea', key: testKey})}
        />
      </Clipboard>
    </div>
  );
}

export interface CopySelectionModalProps {
  additionalHelpText?: string;
  onRequestClose: () => void;
  bases: string;
  nucleotideType: NucleotideType;
  shouldRenderHelmOption: boolean;
  shouldRenderOriginalModeOption: boolean;
  modifiedNucleotides: Readonly<Array<Readonly<ModifiedNucleotideAttributes>>>;
  areSequenceFeatureOptionsApplicable: boolean;
  isOligo: boolean;
  initialSelect: CopyNucleotideSequenceMode;
  copyHandler: (
    annotationsChecked: boolean,
    notContainedAnnotationsChecked: boolean,
    selectedCopyClass: CopyNucleotideSequenceMode,
    copiedResidues: string
  ) => void;
}

export default function CopySelectionModal({
  additionalHelpText,
  onRequestClose,
  bases,
  nucleotideType,
  shouldRenderHelmOption,
  shouldRenderOriginalModeOption,
  modifiedNucleotides,
  areSequenceFeatureOptionsApplicable,
  isOligo,
  initialSelect,
  copyHandler,
}: CopySelectionModalProps): JSX.Element | null {
  const [annotationsPref, setAnnotationsPref] = usePreference<boolean>(CopyPreferences.ANNOTATIONS);
  const [annotationsNotContainedPref, setAnnotationsNotContainedPref] = usePreference<boolean>(
    CopyPreferences.ANNOTATIONS_NOT_CONTAINED
  );
  const monomersResult = useSkippableBenchlingQuery<MonomerQuery>(MONOMER_QUERY, {
    skip: !shouldRenderHelmOption,
  });
  let helm: string | undefined;

  switch (monomersResult.type) {
    case QueryResultType.SUCCESS: {
      const {monomers} = monomersResult.data;
      helm = generateSimplePolymerHELM(bases, nucleotideType, modifiedNucleotides, monomers);
      break;
    }
    case QueryResultType.PENDING: {
      // oxlint-disable-next-line jsx-pascal-case
      return <BenchlingQuery.DEFAULT_RENDER_LOADING />;
    }
    case QueryResultType.ERROR: {
      notifications.error(
        'Encountered issue preparing to copy modified nucleotides; please try operation again.'
      );
      onRequestClose();
      return null;
    }
    case QueryResultType.SKIPPED:
    default: {
      break;
    }
  }

  const notifyCopied = (copyType: CopyNucleotideSequenceMode, copiedString: string): void => {
    copyHandler(annotationsPref, annotationsNotContainedPref, copyType, copiedString);
    onRequestClose();
  };

  let dnaBases;
  let rnaBases;

  if (nucleotideType === NucleotideType.DNA) {
    dnaBases = bases;
    rnaBases = dnaToRNA(bases);
  } else {
    dnaBases = rnaToDNA(bases);
    rnaBases = bases;
  }

  const dnaRow = [
    {
      copyType: CopyNucleotideSequenceMode.DNA,
      label: shouldRenderOriginalModeOption ? 'Sequence as DNA' : 'DNA sequence',
      value: dnaBases,
      testKey: 'dnaSequence',
    },
    {
      copyType: CopyNucleotideSequenceMode.DNA_REVERSE_COMPLEMENT,
      label: 'DNA reverse complement',
      value: revcomp(dnaBases, DNA_COMPLEMENT_TABLE),
      testKey: 'dnaReverseSequence',
    },
  ];
  const aaRow = [
    {
      copyType: CopyNucleotideSequenceMode.TRANSLATION,
      label: 'AA translation',
      value: translateOnStrand(bases, 1, nucleotideType, CodonTranslationTable.STANDARD, false, true),
      testKey: 'aaTranslation',
    },
    {
      copyType: CopyNucleotideSequenceMode.REVERSE_COMPLEMENT_TRANSLATION,
      label: 'AA reverse translation',
      value: translateOnStrand(bases, -1, nucleotideType, CodonTranslationTable.STANDARD, false, true),
      testKey: 'aaReverseTranslation',
    },
  ];
  const rnaRow = [
    {
      copyType: CopyNucleotideSequenceMode.RNA,
      label: shouldRenderOriginalModeOption ? 'Sequence as RNA' : 'RNA sequence',
      value: rnaBases,
      testKey: 'rnaSequence',
    },
    {
      copyType: CopyNucleotideSequenceMode.RNA_REVERSE_COMPLEMENT,
      label: 'RNA reverse complement',
      value: revcomp(rnaBases, RNA_COMPLEMENT_TABLE),
      testKey: 'rnaReverseComplement',
    },
  ];

  // Swap ordering of rows depending on sequence nucleotide type. However, we should only swap the ordering
  // for oligos, and the ordering for sequences should always be the same.
  const copyRows: Array<Array<CopyOption>> =
    nucleotideType === NucleotideType.DNA || !isOligo ? [dnaRow, aaRow, rnaRow] : [rnaRow, aaRow, dnaRow];
  if (shouldRenderOriginalModeOption) {
    copyRows.unshift([
      {
        copyType: CopyNucleotideSequenceMode.ORIGINAL,
        label: 'Original sequence (incl. chemical modifications)',
        value: getUTFlexibleNaturalBasesIfEnabled(
          bases,
          modifiedNucleotides,
          nucleotideType,
          SequenceType.OLIGO
        ),
        testKey: 'originalSeq',
        fullWidth: true,
      },
    ]);
  }
  if (helm) {
    copyRows.push([
      {
        copyType: CopyNucleotideSequenceMode.HELM,
        label: 'HELM',
        value: helm,
        testKey: 'helm',
        fullWidth: shouldRenderOriginalModeOption,
      },
    ]);
  }

  return (
    <Modal
      onRequestClose={onRequestClose}
      title="Copy special"
      dataTestAttrs={{component: 'CopySelectionModal', element: 'root'}}
    >
      <div id="selection-modal">
        <div className="modal-body">
          <p>{additionalHelpText}</p>
          <p>Click the sequence type you would like to copy.</p>
          {copyRows.map((copyRow, i) => (
            <div className="row" key={i}>
              {copyRow.map((copyData) => (
                <CopyContainer
                  {...copyData}
                  initialSelect={initialSelect}
                  notifyCopied={notifyCopied}
                  key={copyData.label}
                />
              ))}
            </div>
          ))}
          {areSequenceFeatureOptionsApplicable && (
            <div className="row">
              <div className="col col-md-12">
                <p>
                  Additional options when copying the sequence or its reverse complement inside Benchling:
                </p>
                <div className="checkbox">
                  <input
                    type="checkbox"
                    checked={annotationsPref}
                    onChange={(e: InputChangeEvent) => {
                      setAnnotationsPref(e.target.checked);
                    }}
                    className="annotations"
                  />
                  Include annotations/translations
                </div>
                <div className="checkbox">
                  <input
                    type="checkbox"
                    disabled={!annotationsPref}
                    checked={annotationsNotContainedPref}
                    onChange={(e: InputChangeEvent) => {
                      setAnnotationsNotContainedPref(e.target.checked);
                    }}
                    className="not-contained-annotations"
                  />
                  Include annotations/translations not fully contained by selection
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
